import client from '@/lib/axios-client'
import type {
	CampaignAdmin,
	CampaignAdminList,
	CampaignAnswerDraft,
	CampaignInput,
	CampaignQuestion,
	CampaignReader,
	CampaignReaderListItem,
	CampaignResponseList,
	CampaignResults,
	CampaignResultsParams,
	CampaignStatus,
} from '@/types/campaign'

const CAMPAIGNS_URL = '/campaigns'
const CAMPAIGNS_URL_ADMIN = '/campaigns/admin'

export async function getMyCampaigns(): Promise<CampaignReaderListItem[]> {
	const response = await client.get(CAMPAIGNS_URL)
	return response.data.data
}

export async function getCampaignForReader(
	id: string,
): Promise<CampaignReader> {
	const response = await client.get(`${CAMPAIGNS_URL}/${id}`)
	return response.data.data
}

/** Respostas vão em multipart: o JSON com os valores e, à parte, os arquivos das perguntas FILE */
export async function submitCampaignResponse(
	id: string,
	questions: CampaignQuestion[],
	answers: Record<string, CampaignAnswerDraft>,
): Promise<{ id: string }> {
	const formData = new FormData()
	let fileIndex = 0

	const payload = questions.flatMap((question) => {
		const answer = answers[question.id]
		if (!answer) return []
		const { file, ...values } = answer
		if (!file) return [{ questionId: question.id, ...values }]
		formData.append('files', file)
		return [{ questionId: question.id, fileIndex: fileIndex++ }]
	})
	formData.append('answers', JSON.stringify(payload))

	const response = await client.post(
		`${CAMPAIGNS_URL}/${id}/responses`,
		formData,
		{
			headers: { 'Content-Type': 'multipart/form-data' },
		},
	)
	return response.data.data
}

export async function getCampaignsAdmin(params?: {
	search?: string
	status?: CampaignStatus
	page?: number
	limit?: number
}): Promise<CampaignAdminList> {
	const response = await client.get(CAMPAIGNS_URL_ADMIN, { params })
	return response.data.data
}

export async function getCampaignAdmin(id: string): Promise<CampaignAdmin> {
	const response = await client.get(`${CAMPAIGNS_URL_ADMIN}/${id}`)
	return response.data.data
}

export async function createCampaign(
	data: CampaignInput,
): Promise<CampaignAdmin> {
	const response = await client.post(CAMPAIGNS_URL_ADMIN, data)
	return response.data.data
}

export async function updateCampaign(
	id: string,
	data: Partial<CampaignInput>,
): Promise<CampaignAdmin> {
	const response = await client.put(`${CAMPAIGNS_URL_ADMIN}/${id}`, data)
	return response.data.data
}

export async function updateCampaignStatus(
	id: string,
	status: CampaignStatus,
): Promise<CampaignAdmin> {
	const response = await client.patch(`${CAMPAIGNS_URL_ADMIN}/${id}/status`, {
		status,
	})
	return response.data.data
}

export async function deleteCampaign(id: string): Promise<void> {
	await client.delete(`${CAMPAIGNS_URL_ADMIN}/${id}`)
}

export async function deleteCampaignResponse(
	campaignId: string,
	responseId: string,
): Promise<void> {
	await client.delete(
		`${CAMPAIGNS_URL_ADMIN}/${campaignId}/responses/${responseId}`,
	)
}

export async function getCampaignResults(
	id: string,
	params?: CampaignResultsParams,
): Promise<CampaignResults> {
	const response = await client.get(`${CAMPAIGNS_URL_ADMIN}/${id}/results`, {
		params,
	})
	return response.data.data
}

export async function getCampaignResponses(
	id: string,
	params?: {
		page?: number
		limit?: number
		startDate?: string
		endDate?: string
	},
): Promise<CampaignResponseList> {
	const response = await client.get(`${CAMPAIGNS_URL_ADMIN}/${id}/responses`, {
		params,
	})
	return response.data.data
}

export async function exportCampaignResponses(
	id: string,
	params: { format: 'xlsx' | 'csv'; startDate?: string; endDate?: string },
): Promise<Blob> {
	const response = await client.get(`${CAMPAIGNS_URL_ADMIN}/${id}/export`, {
		params,
		responseType: 'blob',
	})
	return response.data
}
